import { GetCheckInModalDataResponse } from '@/backend/features/missions/shared/schemas/getCheckInModalDataSchema';
import { BadgeCheckInModalInfoLine } from '@/ui/features/missions/components/BadgeCheckInModalInfoLine';
import { elapsedTime, formatTime } from '@/ui/utils/timeUtils';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BadgeCheckInModalMissionBlockProps {
    data: GetCheckInModalDataResponse;
}

export const BadgeCheckInModalMissionBlock: React.FC<BadgeCheckInModalMissionBlockProps> = ({ data }) => {
    const { t } = useTranslation('rms');

    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: 280,
                [theme.breakpoints.down('md')]: { maxWidth: '100%' },
            })}
        >
            <Box sx={{ p: 2, backgroundColor: themeColors.surface.secondary }}>
                <Typography variant='overline' sx={{ color: themeColors.text.subtle }}>
                    {t('pages.home.checkInModal.mission')}
                </Typography>

                <Typography sx={{ mt: 1, wordBreak: 'break-word' }} variant='h6'>
                    {data.missionName}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, backgroundColor: themeColors.surface.secondary }}>
                <Typography variant='overline' sx={{ color: themeColors.text.subtle, mb: 1 }}>
                    {t('pages.home.checkInModal.shiftDetails')}
                </Typography>

                <BadgeCheckInModalInfoLine
                    name={t('pages.home.checkInModal.shiftInfo.date')}
                    data={data.shiftStartDate ? t('dateWithWeekDay', { value: data.shiftStartDate }) : undefined}
                />

                <BadgeCheckInModalInfoLine
                    name={t('pages.home.checkInModal.shiftInfo.time')}
                    data={t('dateTimeRange', {
                        from: data.shiftStartDate,
                        to: data.shiftEndDate,
                    })}
                />

                <BadgeCheckInModalInfoLine name={t('pages.home.checkInModal.shiftInfo.county')} data={data.shiftCounty} />
            </Box>
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    backgroundColor:
                        (data.shiftCheckInTime && themeColors.accent.orange.subtlest) ||
                        (data.shiftCheckOutTime && themeColors.accent.green.subtlest) ||
                        themeColors.surface.secondary,
                }}
            >
                <Typography variant='overline' sx={{ color: themeColors.text.subtle, mb: 1 }}>
                    {t('pages.home.checkInModal.checkInTime')}
                </Typography>
                <Box>
                    {data.shiftCheckInTime || data.shiftCheckOutTime ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Typography variant='h6'>{t('time', { value: data.shiftCheckInTime || data.shiftCheckOutTime })},</Typography>
                            <Typography
                                variant='h6'
                                sx={{
                                    color:
                                        data.shiftAttendanceStatus === 'checkedIn'
                                            ? themeColors.accent.orange.bold
                                            : themeColors.accent.green.bold,
                                }}
                            >
                                ({data.shiftCheckInTime ? elapsedTime(data.shiftCheckInTime) : formatTime(data.shiftCheckInDuration || 0)})
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant='h6'>-</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
